import React from 'react'
import Collapsible from 'react-collapsible'

// @src imports
import { EditOrderCardRoute } from 'src/orders/routes/EditOrderCardRoute'
import { AddressFragment, CardFragment, CardPaperType } from 'src/graphql'
import { PartialLine } from 'src/helpers/multitouch'
import { ReplaceOrderCardRoute } from 'src/orders/routes/ReplaceOrderCardRoute'
import { AddCardToGiftRoute } from 'src/orders/routes/AddCardToGiftRoute'

// relative imports
import { OrderTypes } from '../../orderType'
import MultiLineRibbon from '../MultiLineRibbon/MultiLineRibbon'
import GiftNoCard from '../GiftNoCard/GiftNoCard'
import ReturnAddress from '../ReturnAddress/ReturnAddress'
import SingleCardDetail from '../SingleCard/SingleCard'
import SendDelayForm from '../SendDelayForm/SendDelayForm'
import { OrderWithPartialLines } from '../../../redux/reducers/orders'
import { Recipients } from 'src/contacts/components'

import styles from 'src/chrome/styles/detailView.module.scss'
import {
  useActions,
  useEffect,
  useMutations,
  useRef,
  useSelector,
  useState,
} from 'src/hooks'
import MobileSingleCardDetail from '../MobileSingleCard/MobileSingleCard'

interface OrderDetailProps {
  shouldBlink: boolean
  order: OrderWithPartialLines
  toggleAddressBlinker: () => void
  onAddCard: () => void
  shouldBlinkAddRecipients?: boolean
  openAddressBook: () => void
  openImportManager: () => void
}

const OrderDetail: React.FC<OrderDetailProps> = props => {
  const {
    shouldBlink,
    order,
    toggleAddressBlinker,
    onAddCard,
    shouldBlinkAddRecipients,
    openAddressBook,
    openImportManager,
  } = props

  // Redux State
  const {
    orderType,
    isCardAdded,
    isGiftAdded,
    activeLine,
    activeLineIndex,
  } = useSelector(state => state.orders)

  // Redux Actions
  const actions = useActions()

  const mutations = useMutations()

  // State
  const mutableModifyingRef = useRef(false)

  const [isPanelOpen, setIsPanelOpen] = useState(
    orderType === OrderTypes.singleCard || isCardAdded || isGiftAdded,
  )

  const [isSendDelayFormOpen, setIsSendDelayFormOpen] = useState(false)
  const [imagesLoaded, setImagesLoaded] = useState(0)
  const [isCardImageLoading, setIsCardImageLoading] = useState(true)
  const [isLineCardImageLoading, setIsLineCardImageLoading] = useState(true)

  // Callbacks
  const modifyOrder = (callback: () => void) => {
    mutableModifyingRef.current = true
    // WARNING: This used to be called asynchronously in the callback of
    // setState, now that we're using `useState` we don't have
    // this functionality anymore.
    callback()
  }

  const closeCollapsible = () => setIsPanelOpen(!isPanelOpen)

  const handleAddGift = () => modifyOrder(actions.openGiftStore)

  const handleEditCard = (card: CardFragment) =>
    modifyOrder(() => {
      actions.openOrder(order.id, EditOrderCardRoute(card.id))
    })

  const handleCardSelect = (line: PartialLine, index: number) => {
    if (activeLineIndex !== index) {
      actions.setActiveLine(line, index)
      setIsCardImageLoading(true)
      setIsPanelOpen(true)
    } else if (index === activeLineIndex || isPanelOpen) {
      closeCollapsible()
    }
  }

  const handleImageLoaded = (type: string) => {
    const imgType = type + 'ImageLoading'

    if (imgType === 'lineCardImageLoading') {
      if (imagesLoaded + 1 === order.lines.length) {
        setIsLineCardImageLoading(false)
        setImagesLoaded(0)
      } else {
        setImagesLoaded(imagesLoaded + 1)
      }
    } else {
      setIsCardImageLoading(false)
    }
  }

  const handleImageError = () => setIsLineCardImageLoading(false)

  const handleUpdatePaperType = async (
    card: CardFragment,
    paperType: CardPaperType,
  ) => {
    await mutations.updateCard({ card: { id: card.id, paperType } })
    actions.updateOrderPaperType(paperType)
  }

  const saveReturnAddress = (returnAddress: AddressFragment) =>
    actions.updateReturnAddress(returnAddress)

  const toggleSendDelayModal = () =>
    setIsSendDelayFormOpen(!isSendDelayFormOpen)

  const isCardInLines = order.lines.some(line => line.card)

  const { isMobile, width } = useSelector(state => state.window)
  const shouldStackDetails = isMobile || width <= 756

  useEffect(() => {
    return () => {
      // eslint-disable-next-line react-hooks/exhaustive-deps
      if (!mutableModifyingRef.current) {
        actions.clearOrder()
      }
    }
  }, [mutableModifyingRef, actions])

  const card = activeLine && activeLine.card

  return (
    <div className={styles.orderView}>
      <div className={styles.previewModal}>
        {isMobile && (
          <>
            <ReturnAddress
              toggleAddressBlinker={toggleAddressBlinker}
              shouldBlink={shouldBlink}
              order={order}
              onSave={saveReturnAddress}
            />
            {order.returnAddress && (
              <section
                className={`${styles.previewSection} ${styles.overviewCard} ${styles.textCenter}`}
              >
                <Recipients
                  order={order}
                  shouldBlinkIfEmpty={shouldBlinkAddRecipients}
                  openAddressBook={openAddressBook}
                  openImportManager={openImportManager}
                />
              </section>
            )}
          </>
        )}
        {isCardInLines ? (
          <MultiLineRibbon
            onCardSelect={handleCardSelect}
            isLineCardImageLoading={isLineCardImageLoading}
            onImageLoaded={handleImageLoaded}
            onImageError={handleImageError}
            onRemoveLine={actions.removeLine}
            onAddLine={onAddCard}
          />
        ) : (
          <GiftNoCard
            order={order}
            handleAddCard={() =>
              actions.openOrder(order.id, AddCardToGiftRoute())
            }
          />
        )}
        {activeLine && card && (
          <div
            style={
              orderType === OrderTypes.singleCard ? {} : { marginTop: '-13px' }
            }
          >
            <Collapsible trigger={''} open={isPanelOpen}>
              {!shouldStackDetails ? (
                <SingleCardDetail
                  order={order}
                  activeLine={activeLine}
                  cardImageLoading={isCardImageLoading}
                  onAddGift={handleAddGift}
                  onClose={closeCollapsible}
                  onDeselectGift={actions.deselectGift}
                  onToggleSendDelay={toggleSendDelayModal}
                  onReplaceCard={cardId => {
                    actions.openOrder(order.id, ReplaceOrderCardRoute(cardId))
                  }}
                  onEditCard={() => handleEditCard(card)}
                  onUpdatePaperType={(paperType: CardPaperType) =>
                    handleUpdatePaperType(card, paperType)
                  }
                />
              ) : (
                <MobileSingleCardDetail
                  order={order}
                  activeLine={activeLine}
                  cardImageLoading={isCardImageLoading}
                  onAddGift={handleAddGift}
                  onClose={closeCollapsible}
                  onDeselectGift={actions.deselectGift}
                  onToggleSendDelay={toggleSendDelayModal}
                  onReplaceCard={cardId => {
                    actions.openOrder(order.id, ReplaceOrderCardRoute(cardId))
                  }}
                  onEditCard={() => handleEditCard(card)}
                  onUpdatePaperType={(paperType: CardPaperType) =>
                    handleUpdatePaperType(card, paperType)
                  }
                />
              )}
            </Collapsible>
          </div>
        )}
        {!isMobile && (
          <>
            <ReturnAddress
              toggleAddressBlinker={toggleAddressBlinker}
              shouldBlink={shouldBlink}
              order={order}
              onSave={saveReturnAddress}
            />
            <section
              className={`${styles.previewSection} ${styles.overviewCard} ${styles.textCenter}`}
            >
              <Recipients
                order={order}
                shouldBlinkIfEmpty={shouldBlinkAddRecipients}
                openAddressBook={openAddressBook}
                openImportManager={openImportManager}
              />
            </section>
          </>
        )}
      </div>
      {activeLine && isSendDelayFormOpen && (
        <SendDelayForm
          isOpen={isSendDelayFormOpen}
          close={toggleSendDelayModal}
          onSubmit={actions.updateSendDelay}
          isMultiLine={order.lines.length > 1}
          sendDelay={activeLine.sendDelay}
        />
      )}
    </div>
  )
}

export default OrderDetail
